import { Injectable } from "@angular/core";
import saveAs from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // For table formatting in jsPDF

const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
const EXCEL_EXTENSION = ".xlsx";

@Injectable({
  providedIn: "root",
})
export class ExportService {
  excel<T extends {}>(json: T[], excelFileName: string): void {
    const worksheet = XLSX.utils.json_to_sheet(json);
    // Calculate column widths based on content
    const columnWidths = this.calculateColumnWidths(json);
    worksheet["!cols"] = columnWidths.map((width) => ({ width }));

    // Add merging for columns in rows where all cell values are identical
    worksheet["!merges"] = this.getMergedColumns(json);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "exported-data");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    this.saveAs(excelBuffer, `${excelFileName}_export_at_${new Date().toLocaleDateString("en-GB")}${EXCEL_EXTENSION}`, EXCEL_TYPE);
  }

  excelFromBlob(data: BlobPart, fileName: string): void {
    this.saveAs(data, `${fileName}${EXCEL_EXTENSION}`, EXCEL_TYPE);
  }

  pdf(data: any, fileName: string): void {
    this.saveAs(data, `${fileName}.pdf`, "application/pdf");
  }

  pdfFromURL(url: string, fileName: string): void {
    saveAs(url, fileName);
  }

  /**
   * @param json `Record<string, unknown>[]`
   * @param pdfFileName `string`
   * @description Generates a PDF from the exported data
   */
  async generatePdfFromData<T extends {}>(json: T[], pdfFileName: string, isEnglish: boolean, title?: string): Promise<void> {
    // Initialize PDF document first
    const doc = new jsPDF({
      orientation: "landscape",
      hotfixes: ["px_scaling"],
      compress: true,
    });

    // Load the font file if needed
    if (!isEnglish) {
      try {
        // Load the font file
        const response = await fetch("assets/fonts/Amiri-Bold.ttf");
        const fontData = await response.arrayBuffer();

        // Convert to base64
        const fontBase64 = btoa(new Uint8Array(fontData).reduce((data, byte) => data + String.fromCharCode(byte), ""));

        // Register the font using the doc instance
        doc.addFileToVFS("Amiri-Bold.ttf", fontBase64);
        doc.addFont("Amiri-Bold.ttf", "Amiri", "normal");
        doc.setFont("Amiri", "normal");
      } catch (error) {
        console.error("Error loading Arabic font:", error);
        // Fallback to default font if Arabic font fails to load
        doc.setFont("helvetica");
      }
    }

    // Step 1: Extract headers and rows
    const headers = Object.keys(json[0] || {});
    const rows = json.map((row) => {
      const rowValues = headers.map((header) => {
        const value = (row as Record<string, any>)[header];
        // Handle null, undefined, and other types
        return value !== null && value !== undefined ? String(value) : "";
      });

      return isEnglish ? rowValues : [...rowValues].reverse();
    });

    // Adjust headers for RTL if needed
    const tableHeaders = isEnglish ? [headers] : [[...headers].reverse()];

    if (title) {
      doc.text(title, doc.internal.pageSize.width / 2, 10, {
        align: "center",
      });
    }

    // Step 2: Configure auto-table
    autoTable(doc, {
      startY: 20,
      head: tableHeaders,
      body: rows,
      theme: "striped",
      showHead: "firstPage", // Only show header on the first page
      styles: {
        font: isEnglish ? "helvetica" : "Amiri",
        fontSize: headers.length > 8 ? 6 : 8,
        cellPadding: 2,
        halign: isEnglish ? "left" : "right",
        valign: "middle",
      },
      didDrawCell: (data) => {
        // Only reverse cell content for body cells in RTL mode
        if (!isEnglish && data.section === "body" && data.cell.text && data.cell.text[0]) {
          data.cell.text[0] = data.cell.text[0].toString().split("").reverse().join("");
        }
      },
      willDrawCell: (data) => {
        if (!isEnglish) {
          data.doc.setFont("Amiri", "normal");
        }
      },
      headStyles: {
        fillColor: [184, 89, 33],
        font: isEnglish ? "helvetica" : "Amiri",
        textColor: [255, 255, 255],
        halign: isEnglish ? "left" : "right",
        overflow: "linebreak",
        minCellWidth: 15,
      },

      columnStyles: {
        0: { cellWidth: "auto" },
      },
      tableWidth: "auto",
      margin: { left: 10, right: 10 },
    });

    doc.save(`${pdfFileName}.pdf`);
  }

  // Function to get merged columns for rows with identical values
  private getMergedColumns(json: any[]): XLSX.Range[] {
    const merges: XLSX.Range[] = [];

    json.forEach((row, rowIndex) => {
      const values = Object.values(row); // Get all cell values in the row

      // Check if all values in the row are identical
      const isIdentical = values.every((value) => value === values[0]);

      if (isIdentical) {
        // Merge all columns in this row
        merges.push({
          s: { r: rowIndex + 1, c: 0 }, // Start cell (row, column)
          e: { r: rowIndex + 1, c: values.length - 1 }, // End cell (row, column)
        });
      }
    });

    return merges;
  }

  /**
   * @param data `Record<string, unknown>[]`
   * @returns `number[]`
   * @description Calculate column widths based on content
   */
  private calculateColumnWidths<T extends Record<string, unknown>>(data: T[]): number[] {
    let columnWidths: number[] = [];

    data.forEach((row) => {
      Object.keys(row).forEach((key, index) => {
        const columnLength = Math.max(String(row[key]).length, key.length);

        columnWidths[index] = Math.max(columnWidths[index] || 0, columnLength * 1.2); // Adjust the multiplier for a better fit
      });
    });

    return columnWidths;
  }

  /**
   *
   * @param buffer `BlobPart`
   * @param fileName `string`
   * @param type `string`
   * @description Save the file as a blob
   */
  private saveAs(buffer: BlobPart, fileName: string, type: string): void {
    const data: Blob = new Blob([buffer], { type });
    // let url = window.URL.createObjectURL(data);
    saveAs(data, fileName);
  }
}
